import { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import { CheckCircle2, Clock, PlayCircle, AlertCircle, TrendingUp, Calendar } from "lucide-react";
import { toast } from "sonner";

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get("/tasks/stats/summary");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Tasks",
      value: stats?.total || 0,
      icon: TrendingUp,
      color: "bg-blue-50 text-blue-700",
      testId: "stat-total-tasks"
    },
    {
      title: "To Do",
      value: stats?.todo || 0,
      icon: Clock,
      color: "bg-gray-50 text-gray-700",
      testId: "stat-todo"
    },
    {
      title: "In Progress",
      value: stats?.in_progress || 0,
      icon: PlayCircle,
      color: "bg-yellow-50 text-yellow-700",
      testId: "stat-in-progress"
    },
    {
      title: "Completed",
      value: stats?.done || 0,
      icon: CheckCircle2,
      color: "bg-green-50 text-green-700",
      testId: "stat-completed"
    },
    {
      title: "Overdue",
      value: stats?.overdue || 0,
      icon: AlertCircle,
      color: "bg-red-50 text-red-700",
      testId: "stat-overdue"
    },
  ];

  const priorityStats = [
    { label: "High Priority", value: stats?.high_priority || 0, color: "bg-red-100 text-red-700", testId: "stat-high-priority" },
    { label: "Medium Priority", value: stats?.medium_priority || 0, color: "bg-yellow-100 text-yellow-700", testId: "stat-medium-priority" },
    { label: "Low Priority", value: stats?.low_priority || 0, color: "bg-green-100 text-green-700", testId: "stat-low-priority" },
  ];

  return (
    <div className="p-8" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-gray-600" data-testid="dashboard-subtitle">
            Overview of your tasks and productivity
          </p>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.title}
                data-testid={stat.testId}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className={`inline-flex p-3 rounded-lg ${stat.color} mb-4`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-sm font-medium text-gray-600 mb-1">{stat.title}</h3>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              </div>
            );
          })}
        </div>

        {/* Priority Breakdown */}
        <div className="bg-white rounded-xl p-6 border border-gray-200" data-testid="priority-breakdown">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Priority Breakdown</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {priorityStats.map((stat) => (
              <div
                key={stat.label}
                data-testid={stat.testId}
                className={`${stat.color} rounded-lg p-4`}
              >
                <h3 className="text-sm font-medium mb-1">{stat.label}</h3>
                <p className="text-2xl font-bold">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
